import type { GeoJSONLineString, GeoJSONPolygon } from "@sitescene/types";

import {
	buildZoneBoundsFeature,
	DEFAULT_ZONE_BOUNDS_SHAPE,
	featureToGeoJsonLineString,
	featureToGeoJsonPolygon,
	geoJsonLineStringToFeature,
	normalizeZoneBoundary,
	resolveLineZoneOverlay,
	toZoneBoundsOverlay,
	type ZoneBoundsShape,
} from "@sitescene/shared";
import { normalizeZoneColor } from "@sitescene/shared";

import type { ShadowLocation, ShadowZone } from "../../types/location-map";

import { aggregateSensorStatusCounts } from "../../map/markers/aggregate-status";
import {
	createClusterMarkerFeature,
	createSensorMarkerFeature,
} from "../../map/markers/create-marker-feature";
import { spiderfyCoordinates } from "../../map/markers/spiderfy";
import { indexMapSensorsFromTree } from "../sensor/index-map-sensors";
import { isValidCoordinatePair } from "../shared/coordinates";
import {
	applySensorCoordinatesToSensors,
	DEFAULT_ZONE_BOUNDARY_KIND,
	DEFAULT_ZONE_BOUNDARY_MODE,
	getOverlayZoneId,
	normalizeZoneBorderWidth,
	resolveZoneBoundaryKind,
	resolveZoneBoundaryMode,
	type SensorLngLatById,
	sortZonesForMapList,
} from "./location-map-utils";
import { getLocationSetupGaps } from "./location-setup-gaps";

type IndexedMapSensor = ReturnType<typeof indexMapSensorsFromTree>[number];

const centroid = (
	points: Array<[number, number]>,
): [number, number] | undefined => {
	if (points.length === 0) return undefined;
	let lng = 0;
	let lat = 0;
	for (const [pointLng, pointLat] of points) {
		lng += pointLng;
		lat += pointLat;
	}
	return [lng / points.length, lat / points.length];
};

const locationPillCoordinates = (
	location: ShadowLocation,
	sensors: IndexedMapSensor[],
): [number, number] | undefined => {
	const locationCoords = location.coordinates?.coordinates;
	if (isValidCoordinatePair(locationCoords)) return locationCoords;

	return centroid(
		sensors
			.map((sensor) => sensor.geolocation?.coordinates)
			.filter((coords): coords is [number, number] =>
				isValidCoordinatePair(coords),
			),
	);
};

/**
 * True when the overview map can place a cluster pill for this location —
 * same rule as {@link buildLocationClusterMarkers}: location.coordinates or
 * at least one sensor with geolocation.
 */
export const isLocationMappable = (location: ShadowLocation): boolean => {
	const sensors = indexMapSensorsFromTree([location]);
	return locationPillCoordinates(location, sensors) !== undefined;
};

/** Split locations into those with a map position and those without. */
export const partitionLocations = (
	locations: ShadowLocation[],
): { mappable: ShadowLocation[]; unmapped: ShadowLocation[] } => {
	const indexedSensors = indexMapSensorsFromTree(locations);
	const byLocation = new Map<string, IndexedMapSensor[]>();

	for (const sensor of indexedSensors) {
		const group = byLocation.get(sensor.locationId) ?? [];
		group.push(sensor);
		byLocation.set(sensor.locationId, group);
	}

	const mappable: ShadowLocation[] = [];
	const unmapped: ShadowLocation[] = [];

	for (const location of locations) {
		const sensors = byLocation.get(location.id) ?? [];
		if (locationPillCoordinates(location, sensors)) {
			mappable.push(location);
		} else {
			unmapped.push(location);
		}
	}

	return { mappable, unmapped };
};

const groupIndexedSensorsByZone = (
	location: ShadowLocation,
): Map<string, IndexedMapSensor[]> => {
	const indexedSensors = indexMapSensorsFromTree([location]);
	const byZone = new Map<string, IndexedMapSensor[]>();

	for (const sensor of indexedSensors) {
		const group = byZone.get(sensor.zoneId) ?? [];
		group.push(sensor);
		byZone.set(sensor.zoneId, group);
	}

	return byZone;
};

const getZoneDisplayCoordinates = (
	zoneSensors: IndexedMapSensor[],
): Map<string, [number, number]> => {
	const sortedZoneSensors = [...zoneSensors].sort((left, right) =>
		left.id.localeCompare(right.id),
	);

	return spiderfyCoordinates(
		sortedZoneSensors.map((sensor) => ({
			coordinates: sensor.geolocation!.coordinates as [number, number],
			id: sensor.id,
		})),
	);
};

const formatMapSensorFingerprint = (
	sensor: ReturnType<typeof indexMapSensorsFromTree>[number],
): string => {
	const coordinates = sensor.geolocation!.coordinates as [number, number];
	return [
		sensor.id,
		sensor.zoneId,
		sensor.name,
		sensor.type,
		sensor.status,
		coordinates.join(","),
	].join("|");
};

const formatZoneFingerprint = (zone: ShadowZone): string =>
	[
		zone.id,
		zone.name,
		resolveZoneBoundaryMode(zone),
		resolveZoneBoundaryKind(zone),
		zone.boundary ? JSON.stringify(zone.boundary) : "",
		String(normalizeZoneBorderWidth(zone.borderWidth)),
		normalizeZoneColor(zone.color).toUpperCase(),
		typeof zone.sortOrder === "number" ? String(zone.sortOrder) : "",
	].join("|");

/**
 * Stable hash of location fields that affect map markers, overlays, and fit.
 * Excludes poll-only fields (e.g. lastInputReceived) so background refetches
 * do not rebuild the map when geometry and labels are unchanged.
 */
export const getLocationMapDataFingerprint = (
	location: ShadowLocation,
): string => {
	const parts = [location.id];

	const locationCoordinates = location.coordinates?.coordinates;
	if (isValidCoordinatePair(locationCoordinates)) {
		parts.push(`loc:${locationCoordinates.join(",")}`);
	}

	const zoneFingerprints = (location.zones ?? [])
		.map(formatZoneFingerprint)
		.sort();

	const sensorFingerprints = indexMapSensorsFromTree([location])
		.map(formatMapSensorFingerprint)
		.sort();

	return [...parts, ...zoneFingerprints, ...sensorFingerprints].join("\n");
};

/** Stable hash across every location that affects the overview cluster layer. */
export const getLocationsMapDataFingerprint = (
	locations: ShadowLocation[],
): string =>
	locations
		.map((location) => getLocationMapDataFingerprint(location))
		.join("\n---\n");

/**
 * One cluster pill per location for the all-locations overview.
 * Placed at location.coordinates, or the centroid of mapped sensors.
 */
export const buildLocationClusterMarkers = (
	locations: ShadowLocation[],
	options?: {
		/** Location ids that already have at least one installation. */
		locationIdsWithInstallation?: ReadonlySet<string>;
	},
): GeoJSON.Feature[] => {
	const indexedSensors = indexMapSensorsFromTree(locations);
	const byLocation = new Map<string, IndexedMapSensor[]>();
	const locationIdsWithInstallation =
		options?.locationIdsWithInstallation ?? new Set<string>();

	for (const sensor of indexedSensors) {
		const group = byLocation.get(sensor.locationId) ?? [];
		group.push(sensor);
		byLocation.set(sensor.locationId, group);
	}

	const features: GeoJSON.Feature[] = [];
	for (const location of locations) {
		const sensors = byLocation.get(location.id) ?? [];
		const coordinates = locationPillCoordinates(location, sensors);
		if (!coordinates) continue;

		const sensorStatus = aggregateSensorStatusCounts(sensors);
		const missingSetup = getLocationSetupGaps(
			location,
			locationIdsWithInstallation.has(location.id),
		);
		features.push(
			createClusterMarkerFeature({
				coordinates,
				count: sensors.length,
				id: `location-cluster:${location.id}`,
				kind: "locationCluster",
				locationId: location.id,
				missingSetup: missingSetup.length > 0 ? [...missingSetup] : undefined,
				name: location.name,
				sensorOffline: sensorStatus.offline,
				sensorOnline: sensorStatus.online,
				sensorWarning: sensorStatus.warning,
			}),
		);
	}

	return features.sort((left, right) =>
		String(left.id).localeCompare(String(right.id)),
	);
};

/** Sensor markers for a location map, spiderfied within each zone. */
export const buildLocationMapMarkers = (
	location: ShadowLocation,
): GeoJSON.Feature[] => {
	const byZone = groupIndexedSensorsByZone(location);
	const features: GeoJSON.Feature[] = [];

	for (const [, zoneSensors] of byZone) {
		const displayCoords = getZoneDisplayCoordinates(zoneSensors);

		for (const sensor of [...zoneSensors].sort((left, right) =>
			left.id.localeCompare(right.id),
		)) {
			const trueCoordinates = sensor.geolocation!.coordinates as [
				number,
				number,
			];
			const coordinates = displayCoords.get(sensor.id) ?? trueCoordinates;

			features.push(
				createSensorMarkerFeature({
					coordinates,
					id: sensor.id,
					lastInputReceived: sensor.lastInputReceived ?? null,
					locationId: sensor.locationId,
					name: sensor.name,
					status: sensor.status,
					trueCoordinates,
					type: sensor.type,
					zoneId: sensor.zoneId,
				}),
			);
		}
	}

	return features.sort((left, right) =>
		String(left.id).localeCompare(String(right.id)),
	);
};

const zonePillCoordinates = (
	sensors: IndexedMapSensor[],
): [number, number] | undefined =>
	centroid(
		sensors
			.map((sensor) => sensor.geolocation?.coordinates)
			.filter((coords): coords is [number, number] =>
				isValidCoordinatePair(coords),
			),
	);

/**
 * One cluster pill per zone at a selected location — same concept as the
 * dashboard map's zones drill. Coincident zone centroids are spiderfied so
 * each card stays clickable; `trueCoordinates` keeps the real centroid for fit.
 */
export const buildLocationZoneClusterMarkers = (
	location: ShadowLocation,
): GeoJSON.Feature[] => {
	const byZone = groupIndexedSensorsByZone(location);
	const zonePills: Array<{
		coordinates: [number, number];
		count: number;
		id: string;
		name: string;
		sensorOffline: number;
		sensorOnline: number;
		sensorWarning: number;
		zoneId: string;
	}> = [];

	for (const [zoneId, sensors] of byZone) {
		const zone = location.zones?.find((entry) => entry.id === zoneId);
		const coordinates = zonePillCoordinates(sensors);
		if (!coordinates) continue;

		const sensorStatus = aggregateSensorStatusCounts(sensors);
		zonePills.push({
			coordinates,
			count: sensors.length,
			id: `zone-cluster:${zoneId}`,
			name: zone?.name ?? sensors[0]?.zoneName ?? "Zone",
			sensorOffline: sensorStatus.offline,
			sensorOnline: sensorStatus.online,
			sensorWarning: sensorStatus.warning,
			zoneId,
		});
	}

	const displayCoords = spiderfyCoordinates(
		zonePills.map((pill) => ({
			coordinates: pill.coordinates,
			id: pill.id,
		})),
		// Slightly wider than sensor spiderfy — zone cards are larger pills.
		{ radiusDegrees: 0.0007 },
	);

	return zonePills
		.map((pill) =>
			createClusterMarkerFeature({
				coordinates: displayCoords.get(pill.id) ?? pill.coordinates,
				count: pill.count,
				id: pill.id,
				kind: "zoneCluster",
				locationId: location.id,
				name: pill.name,
				sensorOffline: pill.sensorOffline,
				sensorOnline: pill.sensorOnline,
				sensorWarning: pill.sensorWarning,
				trueCoordinates: pill.coordinates,
				zoneId: pill.zoneId,
			}),
		)
		.sort((left, right) => String(left.id).localeCompare(String(right.id)));
};

export type LocationMapOverlayOptions = {
	excludeZoneIds?: ReadonlySet<string>;
	lineBoundaries?: Readonly<Record<string, GeoJSONLineString>>;
	zoneBoundsShape?: ZoneBoundsShape;
};

/** Zone boundary overlays aligned to spiderfied marker display coordinates. */
export const buildLocationMapOverlays = (
	location: ShadowLocation,
	options: LocationMapOverlayOptions = {},
): GeoJSON.Feature[] | undefined => {
	const zoneBoundsShape = options.zoneBoundsShape ?? DEFAULT_ZONE_BOUNDS_SHAPE;
	const excludeZoneIds = options.excludeZoneIds ?? new Set<string>();
	const byZone = groupIndexedSensorsByZone(location);
	const overlays: GeoJSON.Feature[] = [];

	for (const [zoneId, zoneSensors] of byZone) {
		if (excludeZoneIds.has(zoneId)) continue;

		const displayCoords = getZoneDisplayCoordinates(zoneSensors);
		const autoCoordinates = [...displayCoords.values()];
		const zone = location.zones?.find((entry) => entry.id === zoneId);
		const boundaryKind = zone
			? resolveZoneBoundaryKind(zone)
			: DEFAULT_ZONE_BOUNDARY_KIND;

		if (boundaryKind === "line") {
			const line = resolveLineZoneOverlay(
				zone?.boundary ?? null,
				autoCoordinates,
				options.lineBoundaries?.[zoneId],
			);
			if (!line) continue;

			overlays.push(
				toZoneBoundsOverlay(
					zoneId,
					{
						...geoJsonLineStringToFeature(line),
						properties: { boundsSource: "derived" },
					},
					zoneBoundsShape,
					zone?.color,
					normalizeZoneBorderWidth(zone?.borderWidth),
				),
			);
			continue;
		}

		const boundaryMode = zone
			? resolveZoneBoundaryMode(zone)
			: DEFAULT_ZONE_BOUNDARY_MODE;
		const storedBoundary =
			boundaryMode === "custom"
				? (normalizeZoneBoundary(zone?.boundary ?? null) ?? null)
				: null;

		const polygon = buildZoneBoundsFeature({
			coordinates: autoCoordinates,
			fallbackToDerived: boundaryMode !== "custom",
			options: { shape: zoneBoundsShape },
			storedBoundary,
		});
		if (!polygon) {
			continue;
		}

		overlays.push(
			toZoneBoundsOverlay(
				zoneId,
				polygon,
				zoneBoundsShape,
				zone?.color,
				normalizeZoneBorderWidth(zone?.borderWidth),
			),
		);
	}

	if (overlays.length === 0) return undefined;

	// Higher list index paints first (behind); list-front (index 0) paints last (in front).
	const paintOrder = new Map(
		sortZonesForMapList(location.zones ?? []).map((zone, index) => [
			zone.id,
			index,
		]),
	);
	overlays.sort((left, right) => {
		const leftId = getOverlayZoneId(left);
		const rightId = getOverlayZoneId(right);
		const leftIndex = leftId ? (paintOrder.get(leftId) ?? 0) : 0;
		const rightIndex = rightId ? (paintOrder.get(rightId) ?? 0) : 0;
		return rightIndex - leftIndex;
	});

	return overlays;
};

/** Auto-mode hull for the zone being edited, using live sensor coordinate overrides. */
export const buildAutoZoneDrawPolygon = ({
	location,
	sensorCoordinates,
	zone,
	zoneBoundsShape = DEFAULT_ZONE_BOUNDS_SHAPE,
}: {
	location: ShadowLocation;
	sensorCoordinates: SensorLngLatById;
	zone: ShadowZone;
	zoneBoundsShape?: ZoneBoundsShape;
}): GeoJSONPolygon | null => {
	const patchedLocation: ShadowLocation = {
		...location,
		zones: (location.zones ?? []).map((entry) =>
			entry.id !== zone.id
				? entry
				: {
						...entry,
						sensors: applySensorCoordinatesToSensors(
							entry.sensors ?? [],
							sensorCoordinates,
						),
					},
		),
	};

	const overlay = buildLocationMapOverlays(patchedLocation, {
		zoneBoundsShape,
	})?.find((feature) => getOverlayZoneId(feature) === zone.id);

	if (!overlay || overlay.geometry.type !== "Polygon") return null;

	return featureToGeoJsonPolygon(overlay as GeoJSON.Feature<GeoJSON.Polygon>);
};

export const buildAutoZoneDrawLine = ({
	location,
	sensorCoordinates,
	zone,
	zoneBoundsShape = DEFAULT_ZONE_BOUNDS_SHAPE,
}: {
	location: ShadowLocation;
	sensorCoordinates: SensorLngLatById;
	zone: ShadowZone;
	zoneBoundsShape?: ZoneBoundsShape;
}): GeoJSONLineString | null => {
	const patchedLocation: ShadowLocation = {
		...location,
		zones: (location.zones ?? []).map((entry) =>
			entry.id !== zone.id
				? entry
				: {
						...entry,
						boundary: undefined,
						boundaryKind: "line",
						boundaryMode: "auto",
						sensors: applySensorCoordinatesToSensors(
							entry.sensors ?? [],
							sensorCoordinates,
						),
					},
		),
	};

	const overlay = buildLocationMapOverlays(patchedLocation, {
		zoneBoundsShape,
	})?.find((feature) => getOverlayZoneId(feature) === zone.id);

	if (!overlay || overlay.geometry.type !== "LineString") return null;

	return featureToGeoJsonLineString(
		overlay as GeoJSON.Feature<GeoJSON.LineString>,
	);
};

export const getLocationMapCenter = (
	location: ShadowLocation,
	markers: GeoJSON.Feature[],
): [number, number] | undefined => {
	const locationCoordinates = location.coordinates?.coordinates;
	if (isValidCoordinatePair(locationCoordinates)) {
		return locationCoordinates;
	}

	const firstMarker = markers[0];
	if (firstMarker?.geometry.type === "Point") {
		const coordinates = firstMarker.geometry.coordinates;
		if (isValidCoordinatePair(coordinates)) return coordinates;
	}

	return undefined;
};
